// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how the phone Chats list reacts to swipes, tunable in Chat settings >
// Swipe on chats: whether rows swipe at all, what a short and a long swipe does
// each way, the short-swipe tray, how far a swipe goes before it arms, and
// haptics. Stored per device, like the mail and Home swipes.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {makeAutoObservable} from 'mobx';

export type ChatSwipeAction = 'read' | 'pin' | 'mute' | 'archive' | 'none';
export type ChatSwipeDirection = 'left' | 'right';
/** A short swipe (past the first line) or a long one (past the second). */
export type ChatSwipeReach = 'short' | 'long';
export type ChatSwipeDistance = 'short' | 'medium' | 'long';

export const CHAT_SWIPE_ACTIONS: ReadonlyArray<ChatSwipeAction> = ['read', 'pin', 'mute', 'archive', 'none'];

/** Share of the row width the first line (short swipe) sits at. The second line is fixed. */
export const CHAT_SWIPE_DISTANCE_RATIO: Record<ChatSwipeDistance, number> = {short: 0.22, medium: 0.3, long: 0.4};
export const CHAT_SWIPE_LONG_RATIO = 0.65;

/** WhatsApp on iOS: right for read and pin, left for mute and archive. */
const SWIPE_DEFAULTS: Record<ChatSwipeDirection, Record<ChatSwipeReach, ChatSwipeAction>> = {
	right: {short: 'read', long: 'pin'},
	left: {short: 'mute', long: 'archive'},
};

export function normalizeChatSwipeAction(value: unknown, fallback: ChatSwipeAction): ChatSwipeAction {
	return CHAT_SWIPE_ACTIONS.includes(value as ChatSwipeAction) ? (value as ChatSwipeAction) : fallback;
}

class ChatsSwipePreferences {
	/** Off: rows never move; the long-press sheet still has every action. */
	enabled = true;
	rightShort: ChatSwipeAction = SWIPE_DEFAULTS.right.short;
	rightLong: ChatSwipeAction = SWIPE_DEFAULTS.right.long;
	leftShort: ChatSwipeAction = SWIPE_DEFAULTS.left.short;
	leftLong: ChatSwipeAction = SWIPE_DEFAULTS.left.long;
	/** A short swipe left that stops before the first line rests open on a tray of the left actions. */
	trayEnabled = true;
	distance: ChatSwipeDistance = 'medium';
	haptics = true;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void makePersistent(
			this,
			'ChatsSwipePreferences',
			['enabled', 'rightShort', 'rightLong', 'leftShort', 'leftLong', 'trayEnabled', 'distance', 'haptics'],
			{syncAcrossTabs: true},
		);
	}

	swipeAction(direction: ChatSwipeDirection, reach: ChatSwipeReach): ChatSwipeAction {
		const stored =
			direction === 'right'
				? reach === 'short'
					? this.rightShort
					: this.rightLong
				: reach === 'short'
					? this.leftShort
					: this.leftLong;
		return normalizeChatSwipeAction(stored, SWIPE_DEFAULTS[direction][reach]);
	}

	get distanceRatio(): number {
		return CHAT_SWIPE_DISTANCE_RATIO[this.distance] ?? CHAT_SWIPE_DISTANCE_RATIO.medium;
	}

	setEnabled(enabled: boolean): void {
		this.enabled = enabled;
	}

	setSwipeAction(direction: ChatSwipeDirection, reach: ChatSwipeReach, action: ChatSwipeAction): void {
		const value = normalizeChatSwipeAction(action, SWIPE_DEFAULTS[direction][reach]);
		if (direction === 'right') {
			if (reach === 'short') this.rightShort = value;
			else this.rightLong = value;
		} else if (reach === 'short') this.leftShort = value;
		else this.leftLong = value;
	}

	setTrayEnabled(enabled: boolean): void {
		this.trayEnabled = enabled;
	}

	setDistance(distance: ChatSwipeDistance): void {
		this.distance = distance in CHAT_SWIPE_DISTANCE_RATIO ? distance : 'medium';
	}

	setHaptics(enabled: boolean): void {
		this.haptics = enabled;
	}
}

export default new ChatsSwipePreferences();
